import { Controller } from '@hotwired/stimulus';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import XHR from '@uppy/xhr-upload';
import Compressor from '@uppy/compressor';
import DropTarget from '@uppy/drop-target';
import ImageEditor from '@uppy/image-editor';
import Webcam from '@uppy/webcam';

// locales
import French from '@uppy/locales/lib/fr_FR';
import English from '@uppy/locales/lib/en_US';
import German from '@uppy/locales/lib/de_DE';
import Italian from '@uppy/locales/lib/it_IT';
import Spanish from '@uppy/locales/lib/es_ES';

// load the css for uppy
import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import '@uppy/image-editor/css/style.min.css';
import '@uppy/webcam/css/style.css';

// to make turbo calls
import { callTurboStream } from '../components/turbo';

// state variable
let uploadStatus = 'in-progress';

export default class extends Controller {
  static values = {
    picturesPath: String,
    imageLimit: Number,
  };

  static targets = [
    'cancelButton',
  ];

  connect() {
    this.loadUploader();
    this.configureUploader();
    this.setupEventHandling();
  }

  loadUploader() {
    // instanciate a new Uppy instance
    this.uppy = new Uppy({
      allowMultipleUploadBatches: false,
      restrictions: {
        maxNumberOfFiles: this.imageLimitValue,
        maxFileSize: 10485760, // 10mb
        allowedFileTypes: ['image/jpeg', 'image/png', 'image/bmp'],
      },
      locale: this.loadLocale(),
    });
  };

  configureUploader() {
    this.uppy.use(Dashboard, {
      target: '#drag-drop-area',
      inline: true,
      width: 'auto',
      autoProceed: true,
      showProgressDetails: true,
      doneButtonHandler: null,
      fileManagerSelectionType: 'files',
      proudlyDisplayPoweredByUppy: false,
      locale: {
        strings: {
          takePictureBtn: 'Prendre photo',
        },
      },
      hideCancelButton: true,
    });

    this.uppy.use(Webcam, {
      title: 'Caméra',
      modes: ['picture'],
      locale: this.loadLocale(),
      target: Dashboard,
    });

    // disable auto retry to avoid saving duplicate pictures
    // a retry will call a retryAll when there is an error which will resend a create request
    this.uppy.use(XHR, {
      endpoint: this.picturesPathValue,
      headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name=csrf-token]').content },
      shouldRetry: (xhr) => false,
    });

    this.uppy.use(DropTarget, { target: document.body });
    this.uppy.use(ImageEditor, { target: Dashboard });
    this.uppy.use(Compressor, { quality: 0.6 });
  }

  // events handling
  setupEventHandling() {
    this.progressHandling();
    this.errorHandling();
    this.successHandling();
  }

  progressHandling() {
    this.uppy.on('upload', () => {
      this.hideCloseBtn();
      this.showCancelBtn();
    });

    this.uppy.on('file-added', (file) => {
      this.uppy.setFileMeta(file.id, { file_id: file.id });
    });

    this.uppy.on('progress', (progress) => {
      if (progress === 100) {
        this.showCloseBtn();
        this.hideCancelBtn();
      }
    });

    this.uppy.on('complete', (results) => {
      if (uploadStatus === 'canceled') {
        this.uppy.info('Votre téléversement a été annulé', 'error', 5000);
        return;
      }
      callTurboStream(this.picturesPathValue);
    });
  }

  resetPictureModal() {
    uploadStatus = 'canceled';
    this.resetModal();
    this.showCloseBtn();
    this.hideCancelBtn();
  }

  errorHandling() {
    this.uppy.on('upload-error', (file, error, response) => {
      this.showCloseBtn();
      this.hideCancelBtn();

      if (response.status === 406) {
        this.uppy.info('Contenu inapproprié detecté', 'error', 5000);
        this.uppy.removeFile(file.id);
      }
    });
  }

  successHandling() {
    this.uppy.on('upload-success', (file, response) => {
      this.showCloseBtn();
      this.hideCancelBtn();

      if (response.body.error_type === 'inappropriate_content') {
        this.uppy.setFileMeta(file.id, { hasInappropriateContent: true });
        return;
      }

      this.uppy.setFileMeta(file.id, { hasInappropriateContent: false });

      if (uploadStatus === 'canceled' && response.body.picture_id !== undefined) {
        callTurboStream(`${this.picturesPathValue}/${response.body.picture_id}`, { method: 'delete' });
      }
    });
  }

  removeFiles(ids) {
    if (ids && ids.length > 0) {
      ids.forEach(id => {
        this.uppy.info(`${ids.length} contenu inapproprié`, 'error', 5000);
        this.uppy.removeFile(id);
      });
    }
  }

  loadLocale() {
    // uppy_locale is loaded in the window variable in the application.html
    switch (window.uppy_locale) {
      case 'en_US':
        return English;
      case 'de_DE':
        return German;
      case 'it_IT':
        return Italian;
      case 'es_ES':
        return Spanish;
      default:
        return French;
    }
  }

  // event animations
  hideCloseBtn() {
    if ($('.modal__actions-close').length > 0) {
      $('.modal__actions-close').hide();
    }
    if ($('.modal__close').length > 0) {
      $('.modal__close').hide();
    }
  }

  showCloseBtn() {
    if ($('.modal__actions-close').length > 0) {
      $('.modal__actions-close').fadeIn();
    }
    if ($('.modal__close').length > 0) {
      $('.modal__close').fadeIn();
    }
  }

  showCancelBtn() {
    $(this.cancelButtonTarget).removeClass('d-none');
  }

  hideCancelBtn() {
    $(this.cancelButtonTarget).addClass('d-none');
  }

  resetModal() {
    if ($('.add-pictures-btn').length > 0) {
      $('.add-pictures-btn').trigger('click');
    }
  }
}
